import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { ShellProviderProps } from '@aviv-estimation-funnel/shells/src/app/factory';
import { Box } from '@gemini/core';
import { SpacingToken } from '@gemini/tokens';
import { isFrenchPortal } from '@mfe-helpers/portalHelpers';
import Animation from '@mfe-presentation/components/Animation';
import Icon from '@mfe-presentation/components/Icon';
import Identity from '@mfe-presentation/components/Identity';
import Location from '@mfe-presentation/components/Location';
import Profile from '@mfe-presentation/components/Profile';
import StreetAutocomplete from '@mfe-presentation/components/Location/StreetAutocomplete';
import PhoneNumberVerification from '@mfe-presentation/components/PhoneNumberVerification';
import PhoneNumberVerificationCode from '@mfe-presentation/components/PhoneNumberVerificationCode';
import PropertyRating from '@mfe-presentation/components/PropertyRating';
import UpdateForm from '@mfe-presentation/components/UpdateForm';
import CheckboxWrapper from '@mfe-presentation/components/Wrappers/CheckboxWrapper';
import IncrementalWrapper from '@mfe-presentation/components/Wrappers/IncrementalWrapper/IncrementalWrapper';
import RadioCardWrapper from '@mfe-presentation/components/Wrappers/RadioCardWrapper';
import RadioWrapper from '@mfe-presentation/components/Wrappers/RadioWrapper';
import SelectWrapper from '@mfe-presentation/components/Wrappers/SelectWrapper';
import TextFieldWrapper from '@mfe-presentation/components/Wrappers/TextFieldWrapper';
import { EMediaComponent, EStepComponent, EUIComponent } from '@mfe-types/enum';
import TextAreaWrapper from '@mfe-presentation/components/Wrappers/TextAreaWrapper';

export const getFieldComponent = (uiComponent: EUIComponent) => {
    switch (uiComponent) {
        case EUIComponent.Checkbox:
            return CheckboxWrapper;
        case EUIComponent.RadioCard:
            return RadioCardWrapper;
        case EUIComponent.StreetAutocomplete:
            return StreetAutocomplete;
        case EUIComponent.Select:
            return SelectWrapper;
        case EUIComponent.PropertyRating:
            return PropertyRating;
        case EUIComponent.Radio:
            return RadioWrapper;
        case EUIComponent.PhoneNumberVerificationCode:
            return PhoneNumberVerificationCode;
        case EUIComponent.Incremental:
            return IncrementalWrapper;
        case EUIComponent.TextArea:
            return TextAreaWrapper;
        case EUIComponent.TextField:
        default:
            return TextFieldWrapper;
    }
};

const getAnimationComponent = (
    mediaComponent: EMediaComponent,
    data: any,
    padding: SpacingToken = 'spacing.12',
) => {
    return () => (
        <Box padding={padding} width="120px" height="120px">
            <Animation
                key={mediaComponent}
                name={mediaComponent}
                loop={false}
                autoplay={false}
                goToLastFrame
                data={data}
            />
        </Box>
    );
};

export const getMediaComponent = (
    portal: Portal,
    mediaComponent: EMediaComponent | undefined,
    shellContext?: ShellProviderProps,
) => {
    if (isFrenchPortal(portal)) {
        switch (mediaComponent) {
            case undefined:
                return null;
            default:
                return () => <Icon width="60" height="60" name={mediaComponent} />;
        }
    } else {
        switch (mediaComponent) {
            case EMediaComponent.Individual:
                return getAnimationComponent(
                    mediaComponent,
                    shellContext?.animations?.individual,
                    'spacing.8',
                );
            case EMediaComponent.Professional:
                return getAnimationComponent(
                    mediaComponent,
                    shellContext?.animations?.professional,
                    'spacing.8',
                );
            case EMediaComponent.Apartment:
                return getAnimationComponent(mediaComponent, shellContext?.animations?.apartment);
            case EMediaComponent.House:
                return getAnimationComponent(mediaComponent, shellContext?.animations?.house);
            case EMediaComponent.BlockedView:
                return getAnimationComponent(mediaComponent, shellContext?.animations?.blockedView);
            case EMediaComponent.ClearView:
                return getAnimationComponent(mediaComponent, shellContext?.animations?.clearView);
            case EMediaComponent.OutstandingView:
                return getAnimationComponent(
                    mediaComponent,
                    shellContext?.animations?.outstandingView,
                );
            case EMediaComponent.TwoFacades:
                return getAnimationComponent(mediaComponent, shellContext?.animations?.twoFacades);
            case EMediaComponent.ThreeFacades:
                return getAnimationComponent(
                    mediaComponent,
                    shellContext?.animations?.threeFacades,
                );
            case EMediaComponent.FourFacades:
                return getAnimationComponent(mediaComponent, shellContext?.animations?.fourFacades);
            default:
                return null;
        }
    }
};

export const stepComponent = (stepComponent: EStepComponent) => {
    switch (stepComponent) {
        case EStepComponent.Location:
            return Location;
        case EStepComponent.PhoneNumberVerification:
            return PhoneNumberVerification;
        case EStepComponent.Identity:
            return Identity;
        case EStepComponent.UpdateForm:
            return UpdateForm;
        case EStepComponent.Profile:
            return Profile;
        default:
            return null;
    }
};
